/**
 * Trang hồ sơ người dùng — cho phép cập nhật thông tin cá nhân và avatar.
 *
 * Phân nhánh theo vai trò:
 * - Người dùng thường: chỉnh sửa địa chỉ, tỉnh ưa thích, ngân sách; xem preview ngân sách.
 * - Seller/Admin: ẩn phần sở thích; hiển thị badge admin.
 *
 * Luồng chính: đồng bộ form từ user context → validate SĐT/ngân sách → gọi authApi.updateProfile.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorMessage } from '../../components/Common/ErrorMessage';
import { PhoneInput } from '../../components/Common/PhoneInput';
import { authApi } from '../../features/auth/api/authApi';
import { useAuth } from '../../hooks/useAuth';
import { APP_ROUTES } from '../../shared/constants/routes';
import { ADMIN_ROLES } from '../../shared/constants/users';
import { AVATAR_IMAGE_UPLOAD } from '../../shared/constants/files';
import { TEXT } from '../../shared/languages';
import { formatBudgetRange } from '../../shared/utils/formatBudgetRange';
import {
  formatVietnamesePhone,
  getPhoneValidationError,
  normalizeVietnamesePhone,
} from '../../shared/utils/vietnamesePhone';

/** Chuyển chuỗi ngân sách (chỉ số) thành number hoặc null nếu rỗng */
const parseBudgetValue = (value) => {
  const normalized = String(value || '').replace(/\D/g, '');

  if (!normalized) {
    return null;
  }

  return Number(normalized);
};

/** Chuẩn hóa giá trị ngân sách từ API để hiển thị trong input */
const formatBudgetInput = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
};

/** Trang chỉnh sửa hồ sơ cá nhân và upload avatar */
export const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const t = TEXT.profile;
  const common = TEXT.common;
  const nav = TEXT.nav;
  const isSeller = ADMIN_ROLES.includes(user?.role);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
    preferred_province: '',
    budget_min: '',
    budget_max: '',
  });
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [budgetError, setBudgetError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAvatarSaving, setIsAvatarSaving] = useState(false);

  // Đồng bộ form khi user context thay đổi (sau refresh hoặc đăng nhập)
  useEffect(() => {
    if (!user) {
      return;
    }

    setForm({
      name: user.name || '',
      phone: formatVietnamesePhone(user.phone || ''),
      address: user.address || '',
      preferred_province: user.preferred_province || '',
      budget_min: formatBudgetInput(user.budget_min),
      budget_max: formatBudgetInput(user.budget_max),
    });
  }, [user]);

  const updateField = (field, value) => {
    if (field === 'phone') {
      setPhoneError('');
    }

    if (field === 'budget_min' || field === 'budget_max') {
      setBudgetError('');
    }

    setForm((current) => ({ ...current, [field]: value }));
  };

  /** Chuỗi preview ngân sách hiển thị trên sidebar — chỉ áp dụng cho người dùng thường */
  const budgetPreview = useMemo(() => {
    if (isSeller) {
      return null;
    }

    const min = parseBudgetValue(form.budget_min);
    const max = parseBudgetValue(form.budget_max);

    return formatBudgetRange(min, max, {
      notProvided: null,
      budgetFrom: common.budgetFrom,
      budgetUpTo: common.budgetUpTo,
    });
  }, [common.budgetFrom, common.budgetUpTo, form.budget_min, form.budget_max, isSeller]);

  /** Lưu thông tin hồ sơ — validate SĐT và khoảng ngân sách trước khi gọi API */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setMessage('');
    setErrorMessage('');
    setPhoneError('');
    setBudgetError('');

    // Kiểm tra định dạng SĐT Việt Nam
    const phoneValidationKey = getPhoneValidationError(form.phone);

    if (phoneValidationKey) {
      setPhoneError(TEXT.validation[phoneValidationKey]);
      setIsSaving(false);
      return;
    }

    const budgetMin = parseBudgetValue(form.budget_min);
    const budgetMax = parseBudgetValue(form.budget_max);

    // Ngân sách tối đa phải >= ngân sách tối thiểu
    if (!isSeller && budgetMin !== null && budgetMax !== null && budgetMax < budgetMin) {
      setBudgetError(TEXT.validation.budgetRangeInvalid);
      setIsSaving(false);
      return;
    }

    try {
      const payload = {
        name: form.name,
        phone: normalizeVietnamesePhone(form.phone) || null,
      };

      if (!isSeller) {
        payload.address = form.address.trim() || null;
        payload.preferred_province = form.preferred_province.trim() || null;
        payload.budget_min = budgetMin;
        payload.budget_max = budgetMax;
      }

      await authApi.updateProfile(payload);
      await refreshUser();
      setMessage(t.saved);
    } catch {
      setErrorMessage(t.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  /** Xử lý chọn file avatar — kiểm tra MIME type trước khi preview */
  const handleAvatarChange = (event) => {
    const [avatarFile] = Array.from(event.target.files || []);
    event.target.value = '';
    setMessage('');
    setAvatarError('');

    if (!avatarFile) {
      setSelectedAvatar(null);
      return;
    }

    if (!AVATAR_IMAGE_UPLOAD.allowedMimeTypes.includes(avatarFile.type)) {
      setSelectedAvatar(null);
      setAvatarError(t.avatarTypeError);
      return;
    }

    setSelectedAvatar(avatarFile);
  };

  /** Upload avatar qua multipart/form-data */
  const handleAvatarSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setAvatarError('');

    if (!selectedAvatar) {
      return;
    }

    const avatarFormData = new FormData();
    avatarFormData.append('avatar', selectedAvatar);
    setIsAvatarSaving(true);

    try {
      await authApi.updateAvatar(avatarFormData);
      await refreshUser();
      setSelectedAvatar(null);
      setMessage(t.avatarSaved);
    } catch {
      setAvatarError(t.avatarSaveError);
    } finally {
      setIsAvatarSaving(false);
    }
  };

  const initials = user?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <section className="section section-profile">
      <div className="container profile-page">
        <header className="profile-page-header">
          <div>
            <span className="eyebrow">{t.eyebrow}</span>
            <h1>{t.title}</h1>
            <p>{isSeller ? t.descriptionSeller : t.description}</p>
          </div>
          {!isSeller && (
            <Link to={APP_ROUTES.favorites} className="text-link profile-page-header-link">
              {nav.favorites}
            </Link>
          )}
        </header>

        <div className="profile-page-layout">
          <aside className="profile-summary-card">
            <div className="profile-summary-avatar" aria-hidden="true">
              {user?.avatar ? (
                <img src={user.avatar} alt="" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div className="profile-summary-copy">
              <strong>{user?.name || common.notUpdated}</strong>
              <span>{user?.email}</span>
            </div>
            {isSeller && <span className="profile-summary-badge">{nav.admin}</span>}
            <form className="profile-avatar-form" onSubmit={handleAvatarSubmit}>
              <strong>{t.avatarSection}</strong>
              <label className="profile-avatar-picker">
                <input
                  type="file"
                  accept={AVATAR_IMAGE_UPLOAD.accept}
                  onChange={handleAvatarChange}
                  disabled={isAvatarSaving}
                />
                <span>{t.avatarChoose}</span>
              </label>
              <small className="field-hint">
                {selectedAvatar
                  ? t.avatarSelected.replace('{name}', selectedAvatar.name)
                  : AVATAR_IMAGE_UPLOAD.hint}
              </small>
              {avatarError && <span className="field-hint field-hint--error">{avatarError}</span>}
              <button
                className="button outline small"
                type="submit"
                disabled={!selectedAvatar || isAvatarSaving}
              >
                {isAvatarSaving ? t.avatarUploading : t.avatarSave}
              </button>
            </form>
            {budgetPreview && (
              <div className="profile-summary-meta">
                <small>{t.sectionPreferences}</small>
                <strong>{budgetPreview}</strong>
              </div>
            )}
          </aside>

          <form className="profile-form-card" onSubmit={handleSubmit}>
            <ErrorMessage message={errorMessage} />
            {message && <div className="success-box">{message}</div>}

            <section className="profile-form-section">
              <h2>{t.sectionBasic}</h2>
              <div className="profile-form-grid">
                <label>
                  {TEXT.auth.fullName}
                  <input
                    required
                    value={form.name}
                    onChange={(event) => updateField('name', event.target.value)}
                  />
                </label>
                <label>
                  {TEXT.auth.email}
                  <input value={user?.email || ''} disabled readOnly />
                  <span className="field-hint">{t.emailHint}</span>
                </label>
                <label className="profile-form-grid-span">
                  {TEXT.auth.phone}
                  <PhoneInput
                    value={form.phone}
                    onChange={(value) => updateField('phone', value)}
                  />
                  {phoneError && <span className="field-hint field-hint--error">{phoneError}</span>}
                </label>
              </div>
            </section>

            {!isSeller && (
              <section className="profile-form-section">
                <h2>{t.sectionPreferences}</h2>
                <div className="profile-form-grid">
                  <label className="profile-form-grid-span">
                    {t.address}
                    <input
                      value={form.address}
                      placeholder={t.addressPlaceholder}
                      onChange={(event) => updateField('address', event.target.value)}
                    />
                  </label>
                  <label>
                    {t.preferredProvince}
                    <input
                      value={form.preferred_province}
                      placeholder={t.preferredProvincePlaceholder}
                      onChange={(event) => updateField('preferred_province', event.target.value)}
                    />
                  </label>
                  <label>
                    {t.budgetMin}
                    <input
                      inputMode="numeric"
                      value={form.budget_min}
                      placeholder="0"
                      onChange={(event) => updateField('budget_min', event.target.value.replace(/\D/g, ''))}
                    />
                  </label>
                  <label>
                    {t.budgetMax}
                    <input
                      inputMode="numeric"
                      value={form.budget_max}
                      placeholder="0"
                      onChange={(event) => updateField('budget_max', event.target.value.replace(/\D/g, ''))}
                    />
                    <span className="field-hint">{t.budgetHint}</span>
                    {budgetError && <span className="field-hint field-hint--error">{budgetError}</span>}
                  </label>
                </div>
              </section>
            )}

            <div className="profile-form-actions">
              <button className="button" type="submit" disabled={isSaving}>
                {isSaving ? common.saving : t.saveButton}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
